import { useSyncExternalStore } from 'react';

// Staleness awareness. NOT connectivity — that is the live channel's job
// (stream.ts), and conflating the two is what put "No signal. Showing what
// was last seen." on a phone with four bars of data all week (V6 §A).
//
// The mechanism: the service worker stamps `x-mrd-fetched-at` on responses
// it serves from its own cache, and NetworkFirst falls back to that cache
// whenever the network is slower than its timeout. On mobile data one slow
// GET is ordinary, and a job screen fires seven of them every thirty
// seconds. The old store read one stamped response as `online: false` and
// then never cleared it except on a fresh unstamped one, so the banner
// latched on and stayed. A cache hit is a fact about ONE response's age.
// It says nothing about the device.
//
// So this store records only what it can honestly know: when the last
// fresh byte arrived, and whether the reads on screen came from the cache.
// Connectivity comes from the wire.
export interface NetState {
  /** True when the most recent read was served from the SW cache. */
  stale: boolean;
  /** ISO time the data on screen was fetched from the network. */
  lastFetchedAt: string | null;
  /** ISO time of the last transport-level failure, or null. */
  lastFailureAt: string | null;
}

let state: NetState = { stale: false, lastFetchedAt: null, lastFailureAt: null };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function set(next: NetState): void {
  const changed =
    next.stale !== state.stale ||
    next.lastFetchedAt !== state.lastFetchedAt ||
    next.lastFailureAt !== state.lastFailureAt;
  state = next;
  if (changed) emit();
}

export function noteResponse(res: Response): void {
  const fetchedAt = res.headers.get('x-mrd-fetched-at');
  set(
    fetchedAt
      ? { ...state, stale: true, lastFetchedAt: fetchedAt }
      : { stale: false, lastFetchedAt: new Date().toISOString(), lastFailureAt: null },
  );
}

export function noteNetworkFailure(): void {
  set({ ...state, lastFailureAt: new Date().toISOString() });
}

export function useNetState(): NetState {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => state,
    () => state,
  );
}
